import React, { ComponentPropsWithoutRef, ElementType, ReactNode } from "react"

import { cn } from "@/lib/utils"

import {
  getClassTypographyVariant,
  TTypographyVariantKeys,
} from "./typographyConfig"

/**
 * Props for Typography component, allowing for extension of props based on the element type.
 *
 * @template T The HTML element or React component that will be rendered.
 */
type TTypographyProps<T extends ElementType> = {
  variant: TTypographyVariantKeys
  children: ReactNode
  className?: string
  /** The element type to render, defaults to 'p' */
  as?: T
} & ComponentPropsWithoutRef<T>

/**
 * Typography component rendering text according to design system variants.
 *
 * @template T The HTML element or React component that will be rendered.
 * @param {TTypographyProps<T>} props The props for the component.
 * @returns The React component.
 */
const Typography = <T extends ElementType = "p">({
  children,
  variant,
  className,
  as,
  ...props
}: TTypographyProps<T>) => {
  const Component = as || "p"
  const variantStyle = getClassTypographyVariant(variant)

  return (
    <Component className={cn(variantStyle, className)} {...props}>
      {children}
    </Component>
  )
}

export { Typography, type TTypographyProps }
